// @flow
import React from 'react'
import ReactHighcharts from 'react-highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import { round } from '../util/numbers'
import type { StatsType } from '../reducers/analysis_panel'

HighchartsMore(ReactHighcharts.Highcharts)

export type ChartProps = {
    chartData: {
        hist: number[],
        stats: StatsType,
        title: string,
    },
    chartStyle: {
        backgroundColor?: string | null,
        height?: number | null,
        marginBottom?: number,
        marginLeft?: number,
        marginRight?: number,
        marginTop?: number,
        spacingBottom?: number,
        spacingTop?: number,
        width?: number | null,
    },
    iframe?: number,
    iframeOrder?: number[],
    redraw: boolean,
    setFrame?: Function,
    type: 'boxplot' | 'bellcurve'
}

class ChartPanel extends React.Component<ChartProps> {
    static defaultProps = {
        iframe: undefined,
        iframeOrder: undefined,
        redraw: false,
        setFrame: undefined,
    }

    shouldComponentUpdate(newProps: ChartProps) {
        let changed = JSON.stringify(this.props.chartData) !== JSON.stringify(newProps.chartData)
        changed = changed || newProps.redraw
        return changed
    }

    chart: any
    config: any
    getChartOptions(extraOptions: any) {
        const minimalSettings = {
            backgroundColor: null,
            marginBottom: 10,
            spacingBottom: 10,
            marginTop: 0,
            spacingTop: 10,
            marginLeft: 30,
            marginRight: 0,
        }

        return { ...minimalSettings, ...this.props.chartStyle, ...(extraOptions || {}) }
    }

    getBoxPlot() {
        const { stats } = this.props.chartData
        const isPopulation = stats.max > 1
        const floorMin = isPopulation ? stats.min : 0
        const ceilMax = isPopulation ? stats.max : 1
        let config = {}
        // generate statistics series
        if (stats) {
            const data = [
                stats.min,
                stats['1q'],
                stats.median,
                stats['3q'],
                stats.max,
            ]
            // Scatter data is a hack to add dataLabels to bloxplots
            // (https://stackoverflow.com/questions/16766832/highcharts-display-the-labels-min-max-median-etc-in-boxplot)
            const scatterData = [
                { name: data[0], x: -1, y: data[0] },
                { name: data[1], x: 1, y: data[1] },
                { name: data[2], x: -1, y: data[2] },
                { name: data[3], x: 1, y: data[3] },
                { name: data[4], x: -1, y: data[4] },
            ]

            const axisData = [
                { name: 0, x: 0, y: 0 },
                { name: 1, x: 0, y: 1 },
            ]

            const showPlotlines = !isPopulation
            config = {
                chart: this.getChartOptions({ type: 'boxplot' }),
                credits: false,
                title: {
                    text: this.props.chartData ? undefined : this.props.chartData.title,
                },
                xAxis: {
                    visible: false,
                },
                yAxis: {
                    title: {
                        text: 'Value',
                        enabled: false,
                    },
                    min: floorMin,
                    max: ceilMax,
                    floor: floorMin,
                    ceiling: ceilMax,
                    visible: true,
                    labels: {
                        enabled: false,
                    },
                    tickAmount: 0,
                    gridlineWidth: 0,
                    plotLines: showPlotlines ? [{
                        value: 0,
                        color: 'gray',
                        width: 1,
                        label: {
                            text: '0',
                            useHTML: true,
                            align: 'center',
                            style: {
                                color: 'black',
                            },
                            x: -50,
                            y: -0,
                        },
                    }, {}, {
                        value: 1,
                        color: 'lightgray',
                        width: 1,
                        label: {
                            text: '1',
                            useHTML: true,
                            align: 'center',
                            style: {
                                color: 'black',
                            },
                            x: -50,
                            y: 0,
                        },
                    }] : [],
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    backgroundColor: 'rgba(255,255,255,1)',
                    enabled: true,
                    formatter: (e: any) => {
                        const series = e.chart.series[0]
                        const sdata = series.data[0]
                        return `<span> 
                            <b>Max:</b> ${sdata.high}<br/>
                            <b>q3:</b> ${sdata.q3}<br/>
                            <b>Median:</b> ${sdata.median}<br/>
                            <b>q1:</b> ${sdata.q1}<br/>
                            <b>Min:</b> ${sdata.low}<br/>
                            </span>`
                    },
                    positioner: () => ({ x: 10, y: 20 }),
                    shape: 'none',
                },
                plotOptions: {
                    boxplot: {
                        // color: 'black',
                    },
                    line: {
                        dashStyle: 'Dot',
                        color: 'lightgray',
                    },
                    series: {
                        marker: {
                            states: {
                                hover: {
                                    enabled: false,
                                },
                            },
                        },
                    },
                },
                series: [{
                    name: 'R',
                    data: [data],
                }, {
                    name: 'Labels',
                    color: 'black',
                    type: 'scatter',
                    data: scatterData,
                    dataLabels: {
                        align: 'left',
                        enabled: true,
                        // eslint-disable-next-line
                        formatter: function () {
                            return this.point.name
                        },
                        style: {
                            fontWeight: 'normal',
                            fontSize: '10px',
                        },
                        x: 0,
                        y: 10,
                    },
                    marker: {
                        enabled: false,
                    },
                }, {
                    name: 'Axis',
                    color: 'gray',
                    type: 'line',
                    data: axisData,
                    marker: {
                        enabled: false,
                    },
                }],
            }
        }
        return config
    }

    getBellCurve() {
        const floorMin = 0
        const ceilMax = 1
        const { stats, hist } = this.props.chartData

        let seriesData
        let config = {}
        if (stats && hist) {
            // From https://benmccormick.org/2017/05/11/building-normal-curves-highcharts/
            // const range = ((from, to, step) => ((add, arr, v) => add(arr, v, add))((arr, v, add) => (v < to) ? add(arr.concat([v]), v + step, add) : arr, [], from))
            const range = (start, end, unit) => Array.from({ length: ((end - start) / unit) }, (x, i) => start + (i * unit))
            const normalY = (x, mean, stdDev) => ((mean && stdDev) ? Math.exp((-0.5) * (((x - mean) / stdDev) ** 2)) : -1)
            const generatePoints = () => {
                const min = 0
                const max = 1
                const unit = (max - min) / 100
                return range(min, max, unit).map(d => round(d, 4))
            }
            const points = generatePoints()
            seriesData = points.map(x => ({ x, y: round(normalY(x, stats.mean, stats.sd), 3) }))

            // A series for every bucket
            const n = hist.length
            const histSeries = hist.map((v, i) => ({
                name: i / n,
                data: [v / n],
                color: 'white',
                borderWidth: 1,
                borderColor: 'black',
                type: 'bar',
                xAxis: 1,
            }))
            const histCategories = hist.map((v, i) => i / n)
            config = {
                chart: this.getChartOptions({ type: 'area', inverted: true }),
                credits: false,
                title: {
                    text: undefined,
                },
                xAxis: [{
                    visible: false,
                    reversed: false,
                    min: floorMin,
                    max: ceilMax,
                    floor: floorMin,
                    ceiling: ceilMax,
                }, {
                    visible: false,
                    categories: histCategories,
                    opposite: true,
                }],
                yAxis: {
                    title: {
                        text: 'Value',
                        enabled: false,
                    },
                    min: 0,
                    max: 1,
                    visible: false,
                },
                tooltip: {
                    positioner: () => ({ x: 10, y: 20 }),
                    shape: 'none',
                    // eslint-disable-next-line
                    formatter: function () {
                        return `<b>X:</b> ${round(this.y, 2)}<br /><b>Y:</b> ${round(this.x, 2)}`
                    },
                    enabled: true,
                },
                legend: {
                    enabled: false,
                },
                plotOptions: {
                    area: {
                        color: 'white',
                        fillOpacity: 0.3,
                    },
                    bar: {
                        pointPadding: 0,
                        pointWidth: 1,
                        groupPadding: 1,
                    },
                },
                series: [{
                    name: 'Bell',
                    data: seriesData,
                    color: '#2f7ed8',
                }, ...histSeries],
            }
        }
        return config
    }

    render() {
        let config
        if (this.props.type === 'boxplot') {
            config = this.getBoxPlot()
        } else if (this.props.type === 'bellcurve') {
            config = this.getBellCurve()
        }

        const style = {
            height: this.props.chartStyle.height || '100%',
            width: this.props.chartStyle.width,
            marginTop: '0px',
            marginBottom: '20px',
            display: 'inline-block',
            backgroundColor: 'transparent',
        }

        if (config) {
            return (
                <div style={style}>
                    <ReactHighcharts config={config} ref={r => (this.chart = r)} />
                </div>
            )
        }

        return (
            <div id="er2_chart">
                <h4 style={{ textAlign: 'center' }}>No data available for plot</h4>
            </div>
        )
    }
}


export default ChartPanel
